import { useState } from "react";
import { homeService } from "@miplatform/api";
import { HTTP_STATUSES } from "@miplatform/constants";
import { useComponentState } from "@miplatform/hooks";
import {
  IApiRequest,
  IApiToastMessageConfig,
  ICreateInspectionAuthorityRequest,
  IInspectionAuthorityResponse,
} from "@miplatform/types";

export const useInspectionAuthorities = () => {
  const [inspectionAuthorities, setInspectionAuthorities] = useState<
    IInspectionAuthorityResponse[]
  >([]);
  const [recentInspectionAuthorities, setRecentInspectionAuthorities] =
    useState<IInspectionAuthorityResponse[]>([]);

  const stateManager = useComponentState();

  const getInspectionAuthorities = async ({
    top,
    skip,
    filters,
    search = "",
  }: IApiRequest = {}) => {
    const searchFilter = search ? { name: { contains: search } } : {};

    const combinedFilters = {
      ...filters,
      ...searchFilter,
    };

    return stateManager.run(async () => {
      const response = await homeService.inspectionAuthorities.getAll({
        top,
        skip,
        sort: "name",
        filters: combinedFilters,
      });
      if (response.status === HTTP_STATUSES.OK) {
        setInspectionAuthorities(response.data);
      }
      return response;
    });
  };

  const getRecentInspectionAuthorities = ({
    sort,
    top,
    filters,
    onSuccess,
  }: IApiRequest) => {
    return stateManager.run(async () => {
      const response = await homeService.inspectionAuthorities.getAll({
        sort,
        top,
        filters,
      });
      if (response.status === HTTP_STATUSES.OK) {
        setRecentInspectionAuthorities(response.data);
        onSuccess?.(response.data);
      }
      return response;
    });
  };

  const getInspectionAuthorityById = async (id: string) => {
    return stateManager.run(async () => {
      return homeService.inspectionAuthorities.getById({ id });
    });
  };

  const createInspectionAuthority = async ({
    data,
  }: {
    data: ICreateInspectionAuthorityRequest;
  }) => {
    const toastMessageConfig: IApiToastMessageConfig = {
      success: { text: `${data.name} created successfully.` },
      failure: { text: `Failed to create ${data.name}. Please try again.` },
    };
    return stateManager.run(async () => {
      return homeService.inspectionAuthorities.create({
        data,
        toastMessageConfig,
      });
    });
  };

  const updateInspectionAuthority = async ({
    id,
    data,
  }: {
    id: string;
    data: IInspectionAuthorityResponse;
  }) => {
    const toastMessageConfig: IApiToastMessageConfig = {
      success: { text: `${data.name} updated successfully.` },
      failure: { text: `Failed to update ${data.name}. Please try again.` },
    };
    return stateManager.run(async () => {
      return homeService.inspectionAuthorities.update({
        id,
        data: { ...data },
        toastMessageConfig,
      });
    });
  };

  const deleteInspectionAuthority = async ({
    id,
    modifiedOn,
    name,
  }: {
    id: string;
    modifiedOn: string | undefined;
    name?: string;
  }) => {
    const toastMessageConfig: IApiToastMessageConfig = {
      success: {
        text: `${name ?? "Inspection Authority"} deleted successfully.`,
      },
      failure: {
        text: `Failed to delete ${
          name ?? "Inspection Authority"
        }. Please try again.`,
      },
    };
    return stateManager.run(async () => {
      return homeService.inspectionAuthorities.delete({
        id,
        modifiedOn,
        toastMessageConfig,
      });
    });
  };

  return {
    inspectionAuthorities,
    setInspectionAuthorities,
    recentInspectionAuthorities,
    setRecentInspectionAuthorities,
    getInspectionAuthorities,
    getRecentInspectionAuthorities,
    deleteInspectionAuthority,
    createInspectionAuthority,
    updateInspectionAuthority,
    isLoading: stateManager.isBusy(),
    getInspectionAuthorityById,
  };
};
